import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Home } from "lucide-react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/orders/${orderId}`)
      .then((r) => setOrder(r.data))
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [orderId]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar showCartButton={false} />
      <div className="max-w-2xl mx-auto px-6 py-16">
        {loading ? (
          <p className="text-center text-muted-foreground">Loading…</p>
        ) : !order ? (
          <p className="text-center text-muted-foreground">Order not found.</p>
        ) : (
          <div className="bg-white rounded-3xl border border-border/60 p-10 text-center" data-testid="order-confirmation">
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
              <CheckCircle2 className="h-8 w-8 text-primary" strokeWidth={1.5} />
            </div>
            <h1 className="font-display text-3xl tracking-tight mb-2">Order Confirmed</h1>
            <p className="text-muted-foreground">Thanks {order.customer_name} — we're on it.</p>

            <div className="mt-8 p-5 rounded-2xl bg-muted/50 border border-border/60 text-left">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs tracking-[0.25em] uppercase text-muted-foreground">Order #</span>
                <span data-testid="order-number" className="font-display text-lg">{order.order_number}</span>
              </div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs tracking-[0.25em] uppercase text-muted-foreground">Type</span>
                <span className="capitalize">{order.order_type}</span>
              </div>
              <div className="space-y-2 pt-3 border-t border-border/60">
                {order.items.map((item, idx) => (
                  <div key={idx} className="flex items-start justify-between text-sm">
                    <div>
                      <p>{item.quantity}× {item.name}</p>
                      {item.flavors?.length > 0 && (
                        <p className="text-xs text-muted-foreground">{item.flavors.join(", ")}</p>
                      )}
                    </div>
                    <span>${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/60">
                <span className="font-display">Total</span>
                <span className="font-display text-xl text-primary">${order.total.toFixed(2)}</span>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mt-6">
              {order.order_type === "delivery"
                ? "We'll bring your drinks soon. Pay on delivery."
                : "Your order will be ready in 10–15 minutes. Pay at pickup."}
            </p>

            <Button
              onClick={() => navigate("/")}
              data-testid="back-home-btn"
              className="mt-8 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
            >
              <Home className="h-4 w-4" strokeWidth={1.5} /> Back to Menu
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
